/**
 * src/components/Financial/MoonStateSection.jsx
 * 【完全版：食イベント表示復元 + 月相時刻表示 + ナクシャトラ解釈全文 + 円周チャート】
 */

import React, { useMemo, useState, useEffect } from "react";
import * as Astro from "../../astronomy.js";
import { styles as commonStyles } from "../../utils/styles.js";
import {
  Moon,
  Zap,
  Map,
  Activity,
  Calendar,
  Info,
  Clock,
  TrendingUp,
} from "lucide-react";

// =============================================================================
// 1. 定数・詳細解釈データ (一切の省略なし)
// =============================================================================

const NAKSHATRAS = [
  "Ashwini",
  "Bharani",
  "Krittika",
  "Rohini",
  "Mrigashira",
  "Ardra",
  "Punarvasu",
  "Pushya",
  "Ashlesha",
  "Magha",
  "Purva Phalguni",
  "Uttara Phalguni",
  "Hasta",
  "Chitra",
  "Swati",
  "Vishakha",
  "Anuradha",
  "Jyeshtha",
  "Mula",
  "Purva Ashadha",
  "Uttara Ashadha",
  "Shravana",
  "Dhanishta",
  "Shatabhisha",
  "Purva Bhadrapada",
  "Uttara Bhadrapada",
  "Revati",
];

// 月相の日本語訳（画像のデザインに合わせる）
const PHASE_TRANSLATIONS = {
  "New Moon": "新月",
  "Waxing Crescent": "三日月〜上弦",
  "First Quarter": "上弦の月",
  "Waxing Gibbous": "上弦〜満月",
  "Full Moon": "満月",
  "Waning Gibbous": "十六夜〜下弦の月",
  "Last Quarter": "下弦の月",
  "Waning Crescent": "下弦〜新月",
};

const NAKSHATRA_MARKET_DESCRIPTIONS = {
  Ashwini:
    "始まりとスピードのナクシャトラ。衝動的で即断即決のエネルギーが強く、市場では急騰・急落の初動が起こりやすい。短期筋が動きやすく、先行指標や噂に反応しやすい。",
  Bharani:
    "制限と忍耐、内側に溜めた力を象徴する。相場では緊張感が高まり、過熱と抑圧が同時に存在する。急反転や強制的な調整が起きやすい局面。",
  Krittika:
    "切り分け、浄化、決断のナクシャトラ。不要なものが切り捨てられ、セクター間の資金移動が起こりやすい。強い値動きと明確な方向性が出やすい。",
  Rohini:
    "成長と繁栄を象徴する最も豊穣なナクシャトラの一つ。市場心理は楽観的になりやすく、上昇トレンドが持続しやすいが、過度な欲望による天井形成にも注意。",
  Mrigashira:
    "探索と不安定さのナクシャトラ。材料探し、テーマ物色が活発になり、方向感が定まりにくい。レンジ相場やフェイントが増えやすい。",
  Ardra:
    "嵐と破壊、再構築を象徴。ネガティブニュース、恐怖、急落が起こりやすいが、その後のリバウンドの起点にもなりやすい。ボラティリティが非常に高い。",
  Punarvasu:
    "回復と再生のナクシャトラ。下落後の安心感や買い戻しが入りやすく、市場が落ち着きを取り戻す。トレンドの再開ポイントとして重要。",
  Pushya:
    "安定、保護、育成の象意。堅実な資金が入りやすく、ディフェンシブ銘柄や長期目線が優位。派手さはないが信頼性の高い相場。",
  Ashlesha:
    "絡みつき、策略、隠れた意図を持つナクシャトラ。市場では操作的な動きやフェイクニュースが出やすい。心理戦が強く、慎重な判断が必要。",
  Magha:
    "権威と過去の栄光を象徴。既存の強者や大型銘柄が注目されやすいが、過去の評価に基づく相場になりやすく、転換点を内包する。",
  "Purva Phalguni":
    "享楽と消費のナクシャトラ。楽観と過剰なリスクテイクが起きやすく、天井圏で現れやすい。過熱感のサインとして重要。",
  "Uttara Phalguni":
    "契約と安定的な繁栄。市場では合意形成が進み、トレンドが定着しやすい。中期的な安心感が生まれる。",
  Hasta:
    "器用さと操作性を象徴。短期売買、アルゴリズム的な動きが目立つ。テクニカルが効きやすく、職人の相場。",
  Chitra:
    "創造と構築。新テーマや新技術への注目が集まりやすく、派手な値動きが出る。期待先行型の相場になりやすい。",
  Swati:
    "独立と自由、市場の解放感を示す。ボラティリティが上昇しやすく、個別銘柄がバラバラに動く。風向きが変わりやすい。",
  Vishakha:
    "目標達成への執着。相場では強いトレンドが形成されやすいが、強欲さから極端な動きにもなりやすい。",
  Anuradha:
    "協調と人脈。市場心理が落ち着き、連動性が高まる。調和的な上昇や秩序ある相場になりやすい。",
  Jyeshtha:
    "極限と支配。相場のピークや緊張の極致を示すことが多く、天井形成や急変動に注意が必要。",
  Mula: "根本破壊と真実の暴露。相場ではクラッシュや構造的な転換点が起きやすい。恐怖が最大化しやすいが、大底にもなり得る。",
  "Purva Ashadha":
    "主張と勝利への意志。強気な買いが入りやすく、反発局面で力を発揮するが、思い込みには注意。",
  "Uttara Ashadha":
    "持続的成功と確立。トレンドが完成し、相場の方向性が固まる。長期的な安心感。",
  Shravana:
    "情報と学習。ニュース、声明、発言が市場を動かしやすい。センチメント変化に敏感。",
  Dhanishta:
    "富とリズム。出来高が増えやすく、循環的な資金移動が活発。トレンド加速や分配局面。",
  Shatabhisha:
    "隔離と浄化。市場が一時的に混乱し、先が見えにくくなる。隠れた調整期間。",
  "Purva Bhadrapada":
    "極端さと情熱。投機性が非常に高まり、熱狂と恐怖が交錯する。バブル的動きに注意。",
  "Uttara Bhadrapada":
    "深い安定と沈静化。相場が落ち着き、感情がクールダウンする。大きな流れの最終調整。",
  Revati:
    "完成と旅立ち。サイクルの終わりと次への移行。相場では利確、区切り、新フェーズへの準備期間。",
};

// getSignInfo の修正 (負の数と正規化への対応)
const getSignInfo = function (lon) {
  if (lon === null || lon === undefined || isNaN(lon)) {
    return { name: "---", nameEn: "Aries", degree: 0, icon: "" };
  }

  // 経度を確実に 0 ~ 359.99... の範囲に正規化 (負の数対策)
  var normalizedLon = ((lon % 360) + 360) % 360;

  var signsJP = [
    "牡羊座",
    "牡牛座",
    "双子座",
    "蟹座",
    "獅子座",
    "乙女座",
    "天秤座",
    "蠍座",
    "射手座",
    "山羊座",
    "水瓶座",
    "魚座",
  ];
  var signsEN = [
    "Aries",
    "Taurus",
    "Gemini",
    "Cancer",
    "Leo",
    "Virgo",
    "Libra",
    "Scorpio",
    "Sagittarius",
    "Capricorn",
    "Aquarius",
    "Pisces",
  ];
  var icons = [
    "♈",
    "♉",
    "♊",
    "♋",
    "♌",
    "♍",
    "♎",
    "♏",
    "♐",
    "♑",
    "♒",
    "♓",
  ];

  // 2. 正規化された経度からインデックス（0-11）を算出
  var index = Math.floor(normalizedLon / 30) % 12;

  return {
    name: signsJP[index],
    nameEn: signsEN[index],
    degree: normalizedLon % 30,
    icon: icons[index] || "",
  };
};

const formatDMS = function (lon) {
  if (lon === null || lon === undefined) return "0°00'00\"";
  var normalized = ((lon % 360) + 360) % 360;
  var d = Math.floor(normalized % 30);
  var m = Math.floor(((normalized % 30) - d) * 60);
  var s = Math.floor((((normalized % 30) - d) * 60 - m) * 60);
  return (
    d +
    "°" +
    m.toString().padStart(2, "0") +
    "'" +
    s.toString().padStart(2, "0") +
    '"'
  );
};

// =============================================================================
// 2. メインコンポーネント
// =============================================================================
export default function MoonStateSection(props) {
  var date = props.transitDate || new Date();
  var isHeliocentric =
    props.isHeliocentric === true || props.isHeliocentric === "true";
  var isSidereal = props.isSidereal === true || props.isSidereal === "true";
  var planetColors = props.planetColors || {};
  var signColors = props.signColors || {};

  const isDark = (function () {
    const p = props.isDarkMode;
    if (p === true || p === "true" || p === "dark") return true;
    if (typeof document !== "undefined") {
      var el = document.documentElement;
      var bd = document.body;
      if (
        el.classList.contains("dark") ||
        bd.classList.contains("dark") ||
        el.getAttribute("data-theme") === "dark"
      )
        return true;
    }
    return false;
  })();
  const isDarkMode = isDark;

  const theme = {
    cardBg: isDark ? "#1c1f26" : "#ffffff",
    subBg: isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
    border: isDark ? "#2d313d" : "#e2e8f0",
    text: isDark ? "#ffffff" : "#1e293b",
    subText: isDark ? "#9ca3af" : "#64748b",
  };

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  useEffect(function () {
    const handleResize = function () {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isMobile = windowWidth <= 1024;
  const isSmallMobile = windowWidth <= 480; // さらに狭い画面用判定

  // 座標・データ計算ロジック（変更なし）
  var ayanamsha = useMemo(
    function () {
      if (!isSidereal) return 0;
      try {
        return Astro.getAyanamsha(date);
      } catch (e) {
        return 24.1;
      }
    },
    [date, isSidereal],
  );

  var sunPos = useMemo(
    function () {
      // ヘリオセントリック時は「太陽」は中心(0°)だが、便宜上「地球」の経度を基準点とする
      var target = isHeliocentric ? "Earth" : "Sun";
      var raw = Astro.getPlanetLongitude(target, date, isHeliocentric);
      var corrected = isSidereal ? raw - ayanamsha : raw;
      return ((corrected % 360) + 360) % 360;
    },
    [date, isHeliocentric, isSidereal, ayanamsha],
  );

  var moonPos = useMemo(
    function () {
      // 月は常に地心（ジオセントリック）で計算（位相・ボイド判定のため）
      var raw = Astro.getPlanetLongitude("Moon", date, false);
      var corrected = isSidereal ? raw - ayanamsha : raw;
      return ((corrected % 360) + 360) % 360;
    },
    [date, isSidereal, ayanamsha],
  );

  var arc = useMemo(
    function () {
      return (moonPos - sunPos + 360) % 360;
    },
    [sunPos, moonPos],
  );

  var data = useMemo(
    function () {
      if (date === null || date === undefined) return null;
      try {
        var events = Astro.getCelestialEvents(date, {
          isHeliocentric: isHeliocentric,
          isSidereal: isSidereal,
        });
        var moonSign = getSignInfo(moonPos);
        var nakshatraIndex = Math.floor(moonPos / (360 / 27)) % 27;
        var currentNakshatra = NAKSHATRAS[nakshatraIndex];
        var vocData = Astro.getMoonVOC(date, isHeliocentric, isSidereal);
        var distanceData = Astro.getMoonDistance(date);
        var apsisEvents = Astro.getLunarApsis(date, 30, 30);
        var moonAge = (arc / 360) * 29.53;
        return {
          events,
          currentNakshatra,
          moonSign,
          voc: vocData,
          distance: distanceData,
          apsis: apsisEvents,
          moonAge,
        };
      } catch (e) {
        return null;
      }
    },
    [date, moonPos, sunPos, arc, isHeliocentric, isSidereal],
  );

  if (!data || !data.events) {
    return (
      <div style={commonStyles.blockCard}>
        <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>
          月の状態を計算中...
        </div>
      </div>
    );
  }

  var events = data.events;
  var nextPhases = events.nextPhases || [];
  var eclipseEvents = events.eclipses || [];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(12, 1fr)",
        gap: "20px",
      }}
    >
      {/* 1. ブロック：現在の月 (レスポンシブ完全修正版) */}
      <div
        style={{
          gridColumn: isMobile ? "span 12" : "span 7",
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: isMobile ? "12px 16px" : "18px 24px",
            borderBottom: "1px solid " + theme.border,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: "bold",
              fontSize: isSmallMobile ? "14px" : "15px",
              color: theme.text,
            }}
          >
            <Moon size={18} color={isDark ? "#fff" : "#2c2d96"} />{" "}
            <span>
              現在の月 {isHeliocentric && <small style={{ fontWeight: "normal", color: theme.subText }}>(地心/Geo基準)</small>}
            </span>
          </div>
          <div style={{ fontSize: "11px", color: theme.subText }}>
            {date.toLocaleString("ja-JP", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>

        <div
          style={{
            padding: isMobile ? "20px 15px" : "30px", // 余白を削減
            display: "flex",
            flexDirection: isMobile ? "column" : "row", // モバイル時は縦並び
            gap: isMobile ? "20px" : "30px",
            alignItems: "center",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* 左：月の満ち欠け描画 */}
          <div style={{ flex: "1", textAlign: "center", width: "100%" }}>
            <div
              style={{
                position: "relative",
                width: isSmallMobile ? "120px" : "160px",
                height: isSmallMobile ? "120px" : "160px",
                margin: "0 auto",
              }}
            >
              <svg width="100%" height="100%" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="#1e293b" />
                {(function () {
                  var fraction = data.events.moonIllumination.fraction;
                  var isWaning = arc > 180;
                  var rx = 45 * Math.abs(1 - 2 * fraction);
                  var sweep = fraction > 0.5 ? 1 : 0;
                  var dPath = !isWaning
                    ? "M 50 5 A 45 45 0 1 1 50 95 A " +
                    rx +
                    " 45 0 1 " +
                    sweep +
                    " 50 5"
                    : "M 50 5 A " +
                    rx +
                    " 45 0 1 " +
                    (1 - sweep) +
                    " 50 95 A 45 45 0 1 1 50 5";
                  return <path d={dPath} fill="#fde68a" />;
                })()}
              </svg>
            </div>
            <div style={{ marginTop: isMobile ? "15px" : "20px" }}>
              <div
                style={{
                  fontSize: isSmallMobile ? "18px" : "20px",
                  fontWeight: "bold",
                  color: isDark ? "#60a5fa" : "#2c2d96",
                }}
              >
                {data.events.moonPhase.name}
              </div>
              <div style={{ fontSize: "12px", color: theme.subText }}>
                ({PHASE_TRANSLATIONS[data.events.moonPhase.name]})
              </div>
            </div>
          </div>

          {/* 右：詳細リスト (見切れ防止の調整) */}
          <div style={{ flex: "1.2", width: "100%" }}>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {(function () {
                var s = data.moonSign;
                var sColor = signColors[s.nameEn] || "#8b5cf6";
                var items = [
                  {
                    label: "獣帯",
                    value: (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <span
                          style={{
                            color: sColor,
                            fontSize: isSmallMobile ? "14px" : "18px",
                          }}
                        >
                          {(s.icon || "") + "\uFE0E"}
                        </span>
                        <span
                          style={{ fontSize: isSmallMobile ? "12px" : "14px" }}
                        >
                          {s.name} {s.degree.toFixed(2)}°
                        </span>
                      </span>
                    ),
                  },
                  { label: "月齢", value: Math.floor(data.moonAge) + " 日" },
                  { label: "経度", value: moonPos.toFixed(2) + "°" }, // 桁数を抑えて幅を節約
                ];
                return items.map(function (item, idx) {
                  return (
                    <div
                      key={idx}
                      style={{
                        background: theme.subBg,
                        padding: isSmallMobile ? "8px 12px" : "12px 16px",
                        borderRadius: "8px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          color: theme.subText,
                          fontWeight: "bold",
                        }}
                      >
                        {item.label}
                      </span>
                      <span
                        style={{
                          fontSize: isSmallMobile ? "12px" : "14px",
                          color: theme.text,
                          fontWeight: "bold",
                        }}
                      >
                        {item.value}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
            <div
              style={{
                background: theme.subBg,
                padding: isSmallMobile ? "12px" : "20px",
                borderRadius: "12px",
                marginTop: "12px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  color: theme.subText,
                  fontWeight: "bold",
                  marginBottom: "4px",
                }}
              >
                輝面比
              </div>
              <div
                style={{
                  fontSize: isSmallMobile ? "28px" : "36px",
                  fontWeight: "900",
                  color: "#10b981",
                }}
              >
                {data.events.moonIllumination.percent}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. ブロック：月のボイドタイム (デザイン・表示完全再現版) */}
      <div
        style={{
          gridColumn: isMobile === true ? "span 12" : "span 5",
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid " + theme.border,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Clock size={18} color={isDark === true ? "#fff" : "#2c2d96"} />
          <span
            style={{ fontWeight: "bold", fontSize: "15px", color: theme.text }}
          >
            月のボイドタイム
          </span>
        </div>

        <div style={{ padding: "30px", flex: 1 }}>
          {/* ボイドタイム状態メッセージ */}
          <div
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              color: theme.text,
              marginBottom: "25px",
            }}
          >
            {data && data.voc && data.voc.isVOC === true
              ? "⚠️ 現在ボイドタイム中です"
              : "現在ボイドタイムではありません"}
          </div>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {/* 項目1：次のボイドまで (または次の星座入りまで) */}
            <div
              style={{
                background: theme.subBg,
                padding: "15px 20px",
                borderRadius: "10px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                border:
                  "1px solid " +
                  (isDark === true ? "transparent" : theme.border),
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  color: theme.subText,
                  fontWeight: "bold",
                }}
              >
                次のボイドまで：
              </span>
              <span
                style={{
                  fontSize: "15px",
                  color: "#10b981",
                  fontWeight: "bold",
                }}
              >
                {(function () {
                  if (!data || !data.voc) return "---";
                  // 概算：次の星座入りまでの時間を表示（画像のデザイン再現）
                  var diffMs = data.voc.estimatedSignChange - date;
                  if (diffMs < 0) return "---";
                  var hours = Math.floor(diffMs / 3600000);
                  var mins = Math.floor((diffMs % 3600000) / 60000);
                  return hours + " 時間 " + mins + " 分";
                })()}
              </span>
            </div>

            {/* 項目2：星座境界まで残り */}
            <div
              style={{
                background: theme.subBg,
                padding: "15px 20px",
                borderRadius: "10px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                border:
                  "1px solid " +
                  (isDark === true ? "transparent" : theme.border),
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  color: theme.subText,
                  fontWeight: "bold",
                }}
              >
                星座境界まで残り：
              </span>
              <span
                style={{
                  fontSize: "15px",
                  color: "#eab308",
                  fontWeight: "bold",
                }}
              >
                {data && data.voc
                  ? data.voc.degreesToSignEnd.toFixed(1)
                  : "0.0"}
                °
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. ブロック：太陽-月アーク (デザイン拡張・完全修正版) */}
      <div
        style={{
          gridColumn: isMobile === true ? "span 12" : "span 6", // 横幅を span 7 に拡張
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid " + theme.border,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Zap size={18} color="#f59e0b" />
            <span
              style={{
                fontWeight: "bold",
                fontSize: "15px",
                color: theme.text,
              }}
            >
              太陽-月アーク (Solar-Lunar Arc)
            </span>
          </div>
        </div>

        <div style={{ padding: "40px 30px", flex: 1, textAlign: "center" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "30px",
            }}
          >
            {/* チャートの表示サイズを大きく (140 -> 240) */}
            <svg width="240" height="240" viewBox="0 0 100 100">
              {/* ガイドライン */}
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke={theme.border}
                strokeWidth="1"
                strokeDasharray="2 2"
              />
              <circle
                cx="50"
                cy="50"
                r="36"
                fill="none"
                stroke={theme.border}
                strokeWidth="2"
              />

              {/* 四方位の目盛り */}
              {[0, 90, 180, 270].map(function (deg) {
                var r = (deg * Math.PI) / 180;
                return (
                  <line
                    key={deg}
                    x1={50 + 34 * Math.cos(r)}
                    y1={50 - 34 * Math.sin(r)}
                    x2={50 + 38 * Math.cos(r)}
                    y2={50 - 38 * Math.sin(r)}
                    stroke={theme.subText}
                    strokeWidth="1"
                  />
                );
              })}

              {/* 太陽シンボル (フォントサイズを大きく 14 -> 24) */}
              {(function () {
                var sRad = (sunPos * Math.PI) / 180;
                var x = 50 + 36 * Math.cos(sRad);
                var y = 50 - 36 * Math.sin(sRad);
                var color = planetColors.Sun || "#FFD700";
                return (
                  <g>
                    <text
                      x={x}
                      y={y}
                      fontSize="24"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill={color}
                    >
                      ☉
                    </text>
                    <text
                      x={50 + 48 * Math.cos(sRad)}
                      y={50 - 48 * Math.sin(sRad)}
                      fontSize="9"
                      textAnchor="middle"
                      fontWeight="bold"
                      fill={theme.subText}
                    >
                      {sunPos.toFixed(1)}°
                    </text>
                  </g>
                );
              })()}

              {/* 月シンボル (フォントサイズを大きく 14 -> 24) */}
              {(function () {
                var mRad = (moonPos * Math.PI) / 180;
                var x = 50 + 36 * Math.cos(mRad);
                var y = 50 - 36 * Math.sin(mRad);
                var color = planetColors.Moon || "#94a3b8";
                return (
                  <g>
                    <text
                      x={x}
                      y={y}
                      fontSize="24"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill={color}
                    >
                      ☽
                    </text>
                    <text
                      x={50 + 48 * Math.cos(mRad)}
                      y={50 - 48 * Math.sin(mRad)}
                      fontSize="9"
                      textAnchor="middle"
                      fontWeight="bold"
                      fill={theme.subText}
                    >
                      {moonPos.toFixed(1)}°
                    </text>
                  </g>
                );
              })()}
            </svg>
          </div>

          {/* 角度の表示 (色指定: rgb(16, 185, 129) -> #10b981) */}
          <div
            style={{ fontSize: "48px", fontWeight: "900", color: "#10b981" }}
          >
            {arc.toFixed(2)}°
          </div>
        </div>
      </div>

      {/* 4. ブロック：月のマンション (デザイン・レイアウト完全修正版) */}
      <div
        style={{
          gridColumn: isMobile === true ? "span 12" : "span 6", // 横幅を span 6 に変更
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* 見出しタイトルは「月のマンション」のみ */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid " + theme.border,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Map size={18} color="#a855f7" />
          <span
            style={{ fontWeight: "bold", fontSize: "15px", color: theme.text }}
          >
            月のマンション
          </span>
        </div>

        <div style={{ padding: "30px", flex: 1 }}>
          {/* ナクシャトラ名を大きなフォントで表示 */}
          <div
            style={{
              fontSize: "24px",
              fontWeight: "900",
              color: theme.text,
              marginBottom: "20px",
              letterSpacing: "0.02em",
            }}
          >
            Nakshatra :{" "}
            {data && data.currentNakshatra ? data.currentNakshatra : "---"}
          </div>

          {/* 解釈テキストのボックス */}
          <div
            style={{
              padding: "20px",
              background: theme.subBg,
              borderRadius: "12px",
              border:
                "1px solid " + (isDark === true ? "transparent" : theme.border),
              textAlign: "left",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: "bold",
                color: "#a855f7",
                marginBottom: "10px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <TrendingUp size={14} /> 市場のエネルギー特性:
            </div>
            <div
              style={{
                fontSize: "14px",
                color: theme.subText,
                lineHeight: "1.8",
              }}
            >
              {data && data.currentNakshatra
                ? NAKSHATRA_MARKET_DESCRIPTIONS[data.currentNakshatra]
                : "詳細データ解析中..."}
            </div>
          </div>
        </div>
      </div>

      {/* 8. ブロック：月の距離 (デザイン・ダークモード完全対応版) */}
      <div
        style={{
          gridColumn: isMobile ? "span 12" : "span 4",
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid " + theme.border,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {/* アイコンの色を rgb(147, 39, 143) に変更 */}
          <Activity size={18} color="rgb(147, 39, 143)" />
          <span
            style={{ fontWeight: "bold", fontSize: "15px", color: theme.text }}
          >
            月の距離 (アポジー／ペリジー)
          </span>
        </div>

        <div style={{ padding: "24px" }}>
          {/* メイン：現在の距離 */}
          <div
            style={{
              padding: "24px",
              background: theme.subBg,
              borderRadius: "16px",
              marginBottom: "20px",
              textAlign: "center",
              border: "1px solid " + (isDark ? "transparent" : theme.border),
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
                marginBottom: "10px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  color: theme.subText,
                  fontWeight: "bold",
                }}
              >
                CURRENT DISTANCE
              </span>
              {data.distance.status !== "average" && (
                <span
                  style={{
                    fontSize: "10px",
                    padding: "2px 10px",
                    borderRadius: "10px",
                    background:
                      data.distance.status === "far"
                        ? "rgba(239, 68, 68, 0.1)"
                        : "rgba(16, 185, 129, 0.1)",
                    color:
                      data.distance.status === "far" ? "#ef4444" : "#10b981",
                    fontWeight: "bold",
                    border:
                      "1px solid " +
                      (data.distance.status === "far" ? "#ef4444" : "#10b981"),
                  }}
                >
                  {data.distance.status === "far" ? "遠地点付近" : "近地点付近"}
                </span>
              )}
            </div>
            <div
              style={{ fontSize: "36px", fontWeight: "900", color: theme.text }}
            >
              {Math.round(data.distance.distance_km).toLocaleString()}
              <span
                style={{
                  fontSize: "16px",
                  marginLeft: "6px",
                  color: theme.subText,
                }}
              >
                km
              </span>
            </div>
          </div>

          {/* サブ：近日中のイベント */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px",
            }}
          >
            {data.apsis.slice(0, 4).map(function (ap, i) {
              const isPerigee = ap.type === "perigee";
              return (
                <div
                  key={i}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    background: theme.subBg,
                    border: "1px solid " + theme.border,
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      color: isPerigee ? "#10b981" : "#ef4444",
                      marginBottom: "4px",
                    }}
                  >
                    {isPerigee ? "● 近地点" : "○ 遠地点"}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "bold",
                      color: theme.text,
                    }}
                  >
                    {ap.date.toLocaleDateString("ja-JP", {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 5. ブロック：今後の月相 (デザイン・ダークモード完全対応版) */}
      <div
        style={{
          gridColumn: isMobile ? "span 12" : "span 4",
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid " + theme.border,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {/* アイコンの色を rgb(147, 39, 143) に変更 */}
          <Calendar size={18} color="rgb(147, 39, 143)" />
          <span
            style={{ fontWeight: "bold", fontSize: "15px", color: theme.text }}
          >
            今後の月相
          </span>
        </div>

        <div style={{ padding: "12px" }}>
          {nextPhases.map(function (p, i) {
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  background: i % 2 === 0 ? "transparent" : theme.subBg,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "15px" }}
                >
                  <span
                    style={{
                      fontSize: "20px",
                      width: "30px",
                      textAlign: "center",
                    }}
                  >
                    {p.symbol}
                  </span>
                  <div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: theme.text,
                      }}
                    >
                      {p.name}
                    </div>
                    <div style={{ fontSize: "11px", color: theme.subText }}>
                      {p.date.toLocaleString("ja-JP", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: theme.subText,
                    fontWeight: "bold",
                  }}
                >
                  {p.angle}°
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 6. ブロック：日食・月食 (デザイン・ダークモード完全対応版) */}
      <div
        style={{
          gridColumn: isMobile ? "span 12" : "span 4",
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid " + theme.border,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Zap size={18} color="#e74c3c" />
          <span
            style={{ fontWeight: "bold", fontSize: "15px", color: theme.text }}
          >
            日食・月食
          </span>
        </div>

        <div style={{ padding: "20px" }}>
          {eclipseEvents !== undefined && eclipseEvents.length > 0 ? (
            eclipseEvents.map(function (ecl, i) {
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    padding: "15px",
                    background: theme.subBg,
                    borderRadius: "12px",
                    border: "1px solid " + theme.border,
                    marginBottom: "10px",
                  }}
                >
                  <span style={{ fontSize: "28px" }}>{ecl.symbol}</span>
                  <div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: theme.text,
                      }}
                    >
                      {ecl.type}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: theme.subText,
                        marginTop: "2px",
                      }}
                    >
                      {ecl.date !== undefined
                        ? ecl.date.toLocaleString("ja-JP", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                        : "---"}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div
              style={{
                padding: "60px 20px",
                textAlign: "center",
                color: theme.subText,
                fontSize: "12px",
              }}
            >
              近日の食イベントはありません。
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
